import { store } from '../../app/store';
import { FilterQueries } from '../../app/utils/filter';
import { handleError } from '../../features/layout/layoutSlice';
import { tEntityId } from '../../models/entity';
import { PaginatedResult } from '../../models/paginatedResult';
import { client } from '../client';
import { FetchArg } from '../rtk-queries/queryApiCreator';
import { EksportSettings } from './../../models/eksportSettings';
import { SearchQueries } from './../rtk-queries/queryApiCreator';

export type NextPageParam = FetchArg<unknown> & {
    endpoint: string,
};
export default class RequestService<T extends { id: string | number }, U extends string> {
    serviceName: string;
    typeName: U;
    idKey?: keyof T;
    capitalizeTypeName: Capitalize<U>;

    constructor(serviceName: string & U) {
        this.serviceName = serviceName;
        this.typeName = serviceName.toLowerCase() as U;
        this.capitalizeTypeName = (serviceName.charAt(0).toUpperCase() + serviceName.slice(1)) as Capitalize<U>;
    }

    get getNextPage() {
        return (param: Omit<NextPageParam, 'endpoint'>) => getNextPageURL({ endpoint: this.serviceName, ...param });
    }

    fetch = async (pageUrl?: string) =>
        client.get<PaginatedResult<T>>(pageUrl ?? this.serviceName);

    filter = async (filter?: FilterQueries, query?: string, pageUrl?: string, disablePagination?: boolean) => {
        return client.post<PaginatedResult<T>>(pageUrl ??
            getQueryURL(`${this.serviceName}/filter`, {
                q: query,
                disablePagination
            }), filter);
    };
    getById = async (id: string | number) => client.get<T>(`${this.serviceName}/${encodeURIComponent(id)}`);

    init = async () => client.get<T>(`${this.serviceName}/init`);

    plainSearch = async (query: string, disablePagination?: boolean) =>
        client.get<PaginatedResult<T>>(getQueryURL(this.serviceName, {
            disablePagination,
            q: query
        }));

    plainPaginatedSearch = async (query: string) => client.get<PaginatedResult<T>>(`${this.serviceName}?q=${encodeURIComponent(query)}`);

    search = async (query: SearchQueries<T>, disablePagination?: boolean, pageUrl?: string) => client.post<PaginatedResult<T>>(pageUrl ?? getQueryURL(`${this.serviceName}/search`, { disablePagination }), query);

    create = async (item: T) => client.post<T>(`${this.serviceName}`, item);

    async update(item: T) {
        const id = this.idKey ? item[this.idKey] : item.id;
        return client.post<T>(`${this.serviceName}/${encodeURIComponent(id as tEntityId)}`, item);
    }

    loadEksport = async (eksportSettings: EksportSettings<T>) => await client.getFile(`${this.serviceName}/eksport`, 'POST', eksportSettings);

    delete = async (id: string | number) => client.delete<void>(`${this.serviceName}/${encodeURIComponent(id)}`);
    //delete = async (id: string | number) => client.delete<void>(`${this.serviceName}/${id}`);
    deleteReport = async (id: string | number) => {
        //return client.get<any>(`${this.serviceName}/DeleteReport/${id}`);
        return client.get<any>(`${this.serviceName}/DeleteReport/${encodeURIComponent(id)}`);
    };
}


export const getNextPageURL = ({
    endpoint,
    param,
    disablePagination,
    query
}: NextPageParam, queryParams?: { [key: string]: string; }) => {
    const { ascending = typeof param?.orderColumn === 'string' ? true : undefined } = param ?? {};

    return getQueryURL(endpoint, { ...param, ascending, q: query, disablePagination, ...queryParams });
};

export const getQueryURL = (endpoint: string, params?: object) => {
    let searchParams = new URLSearchParams();

    if (params) {
        for (const [key, value] of Object.entries(params)) {
            if (value == null) continue;
            if (Array.isArray(value)) {
                value.forEach(val => {
                    searchParams.append(key, val);
                });
                continue;
            }
            searchParams.set(key, value);
        }
    }

    const url = searchParams.toString().length > 0 ? `${endpoint}?${searchParams.toString()}` : endpoint;
    return url;
};

export async function uploadFile(endpoint: string, fileContent: Blob, config?: RequestInit) {
    try {
        const buffer = await fileContent.arrayBuffer();
        return await client.post(endpoint, buffer, config);
    } catch (err) {
        await store.dispatch(handleError(err as Error));
        return null;
    }
}